import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { Button, Checkbox, ActionIcon } from '@mantine/core';
import {
    IconBuildingStore,
    IconEdit,
    IconCashEdit,
    IconPlus,
    IconX,
    IconDeviceFloppy,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { User, Business } from '@turizi/shared';
import { useUpdateUser, useBusinesses } from '@/hooks/api-hooks';
import BusinessSelector from '@/components/business-selector';
import EditionGroupSelector from '@/components/edition-group-selector';
import { TagIcon } from '@/lib/tag-utils';

export interface UserReservationPermissionsTabProps {
    user?: User;
}

export function UserReservationPermissionsTab({ user: propUser }: UserReservationPermissionsTabProps) {
    const { t } = useTranslation();
    const outletContext = useOutletContext<{ user?: User } | null>();
    const user = propUser || outletContext?.user;

    const updateUserMutation = useUpdateUser();
    const { data: businessesResponse } = useBusinesses();
    const allBusinesses = businessesResponse?.data || [];

    const [permissions, setPermissions] = useState<Record<string, any>>(user?.permissions || {});
    const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);

    useEffect(() => {
        if (user?.permissions) {
            setPermissions(user.permissions);
        }
    }, [user?.permissions]);

    if (!user) {
        return null;
    }

    const businessMode = permissions.reservation_business_mode || 'all';
    const editReservationMode = permissions.edit_reservation_mode || 'all';
    const allowedIds: number[] = Array.isArray(permissions.allowed_reservation_business_ids)
        ? permissions.allowed_reservation_business_ids
        : [];

    const handleSave = async (updatedPermissions?: Record<string, any>) => {
        const payloadPermissions = updatedPermissions || permissions;
        const { error } = await updateUserMutation.mutateAsync({
            id: Number(user.id),
            permissions: payloadPermissions,
        });

        if (error) {
            notifications.show({
                title: t('error'),
                message: typeof error === 'string' ? error : error.message || t('unknown_error'),
                color: 'red',
            });
        } else {
            notifications.show({
                title: t('success'),
                message: t('user_updated_successfully'),
                color: 'green',
            });
        }
    };

    const handleBusinessModeChange = (mode: 'all' | 'selected') => {
        const next = {
            ...permissions,
            reservation_business_mode: mode,
        };
        setPermissions(next);
        handleSave(next);
    };

    const handleEditReservationModeChange = (mode: 'all' | 'created_only' | 'group') => {
        const next = {
            ...permissions,
            edit_reservation_mode: mode,
        };
        setPermissions(next);
        handleSave(next);
    };

    const handleAddBusiness = (business: Business) => {
        if (allowedIds.includes(business.id)) return;
        const next = {
            ...permissions,
            allowed_reservation_business_ids: [...allowedIds, business.id],
        };
        setPermissions(next);
        handleSave(next);
    };

    const handleRemoveBusiness = (businessId: number) => {
        const next = {
            ...permissions,
            allowed_reservation_business_ids: allowedIds.filter((bId) => bId !== businessId),
        };
        setPermissions(next);
        handleSave(next);
    };

    return (
        <div className="space-y-5">
            {/* Section 1: Business Access (Clientes para Reservas) */}
            <div className="p-5 rounded-2xl border border-neutral-200/90 bg-neutral-50/50 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-white border border-neutral-200 text-neutral-800 shadow-2xs flex-shrink-0">
                        <IconBuildingStore size={18} />
                    </div>
                    <div>
                        <div className="text-xs font-bold text-neutral-900 uppercase tracking-wider">
                            {t('reservation_business_access')}
                        </div>
                        <div className="text-xs text-neutral-500 leading-relaxed mt-0.5">
                            {t('reservation_business_access_desc')}
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={businessMode === 'all'}
                            onChange={() => handleBusinessModeChange('all')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('all_businesses')}
                        </span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={businessMode === 'selected'}
                            onChange={() => handleBusinessModeChange('selected')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('selected_businesses')}
                        </span>
                    </label>
                </div>

                {businessMode === 'selected' && (
                    <div className="space-y-3 p-4 bg-white rounded-xl border border-neutral-200 shadow-2xs">
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-neutral-700 font-semibold">
                                {t('select_allowed_businesses')} ({allowedIds.length})
                            </div>
                            <Button
                                size="xs"
                                variant="light"
                                color="black"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => setShowAddBusinessModal(true)}
                            >
                                {t('add_business')}
                            </Button>
                        </div>

                        {allowedIds.length === 0 ? (
                            <div className="text-xs text-amber-800 bg-amber-50/90 p-3 rounded-xl border border-amber-200 font-medium">
                                {t('no_businesses_selected')}
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {allowedIds.map((bId) => {
                                    const bus = allBusinesses.find((b) => b.id === bId);
                                    const label = bus ? bus.name : `#${bId}`;
                                    return (
                                        <div
                                            key={bId}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-neutral-50 border border-neutral-200 text-neutral-800 shadow-2xs select-none"
                                        >
                                            <span className="flex items-center gap-1.5 py-0.5">
                                                <TagIcon name={bus?.category || 'business'} hideText size="xs" />
                                                <span>{label}</span>
                                            </span>
                                            <ActionIcon
                                                size="xs"
                                                color="gray"
                                                variant="transparent"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveBusiness(bId);
                                                }}
                                            >
                                                <IconX size={12} />
                                            </ActionIcon>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <BusinessSelector
                    opened={showAddBusinessModal}
                    onClose={() => setShowAddBusinessModal(false)}
                    exclude={allowedIds}
                    onSelect={(item) => {
                        handleAddBusiness(item);
                        setShowAddBusinessModal(false);
                    }}
                />
            </div>

            {/* Section 2: Reservation Edit Access (Edición de Reservas) */}
            <div className="p-5 rounded-2xl border border-neutral-200/90 bg-neutral-50/50 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-white border border-neutral-200 text-neutral-800 shadow-2xs flex-shrink-0">
                        <IconEdit size={18} />
                    </div>
                    <div>
                        <div className="text-xs font-bold text-neutral-900 uppercase tracking-wider">
                            {t('reservation_edit_access')}
                        </div>
                        <div className="text-xs text-neutral-500 leading-relaxed mt-0.5">
                            {editReservationMode === 'all'
                                ? t('all_reservations_desc')
                                : editReservationMode === 'group'
                                    ? t('edition_group_reservations_desc')
                                    : t('only_created_reservations_desc')}
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={editReservationMode === 'all'}
                            onChange={() => handleEditReservationModeChange('all')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('all_reservations')}
                        </span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={editReservationMode === 'created_only'}
                            onChange={() => handleEditReservationModeChange('created_only')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('only_created_reservations')}
                        </span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={editReservationMode === 'group'}
                            onChange={() => handleEditReservationModeChange('group')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('edition_group_reservations')}
                        </span>
                    </label>
                </div>

                {editReservationMode === 'group' && (
                    <div className="p-4 bg-white rounded-xl border border-neutral-200 shadow-2xs">
                        <EditionGroupSelector
                            value={permissions.edition_group}
                            currentUserId={user.id}
                            disabled={updateUserMutation.isPending}
                            onChange={(group) => {
                                const updated = {
                                    ...permissions,
                                    edition_group: group,
                                    edition_groups: group ? [group] : [],
                                };
                                setPermissions(updated);
                                handleSave(updated);
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Section 3: Manual Price & Cost Permission */}
            <div className="p-5 rounded-2xl border border-neutral-200/90 bg-neutral-50/50">
                <div
                    className={`flex items-start gap-3.5 p-4 rounded-xl border transition-all cursor-pointer select-none ${permissions.edit_prices_and_costs_manually !== false
                        ? 'bg-white border-neutral-900 shadow-2xs'
                        : 'bg-white hover:bg-neutral-100/70 border-neutral-200'
                        }`}
                    onClick={() => {
                        if (updateUserMutation.isPending) return;
                        const nextVal = permissions.edit_prices_and_costs_manually === false;
                        const updated = {
                            ...permissions,
                            edit_prices_and_costs_manually: nextVal,
                        };
                        setPermissions(updated);
                        handleSave(updated);
                    }}
                >
                    <div className="pt-0.5 pointer-events-none">
                        <Checkbox
                            size="sm"
                            color="dark"
                            checked={permissions.edit_prices_and_costs_manually !== false}
                            readOnly
                        />
                    </div>
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                        <div className="p-1 rounded-lg bg-neutral-100 text-neutral-800 flex-shrink-0 mt-0.5">
                            <IconCashEdit size={16} />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-neutral-900 uppercase tracking-wider">
                                {t('edit_prices_and_costs_manually')}
                            </div>
                            <div className="text-xs text-neutral-500 leading-relaxed mt-0.5">
                                {t('edit_prices_and_costs_manually_desc')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
                <Button
                    color="black"
                    size="sm"
                    radius="md"
                    loading={updateUserMutation.isPending}
                    onClick={() => handleSave()}
                    leftSection={<IconDeviceFloppy size={16} />}
                >
                    {t('save_changes')}
                </Button>
            </div>
        </div>
    );
}

export default UserReservationPermissionsTab;
